import {DocumentNode} from 'graphql';
import {MutationFunctionOptions} from 'react-apollo';
import {FetchResult} from 'apollo-link';
import {DataProxy} from 'apollo-cache';

import {hasValue} from './typescript';
import {NonEmptyArray} from './non-empty-array';

/**
 * Will return the name of the given Query, Mutation or Subscription component
 */
export const nameOfGraphQLOperation = (operation: DocumentNode) => {
    for (const definition of operation.definitions) {
        if (definition.kind === 'OperationDefinition') {
            if (hasValue(definition.name)) {
                return definition.name.value;
            }
        }
    }
    return null;
};

/**
 * Returns `string[]` containing the names of the given GraphQL operations.
 *
 * Example usage:
 * ```
 * useMutation(Queries.foo, {
 *   refetchQueries: namesOfGraphQLOperations(OtherQueries.bar, OtherQueries.wizbiz)
 * })
 * ```
 */
export const namesOfGraphQLOperations = (...args: NonEmptyArray<DocumentNode>) =>
    args.map(nameOfGraphQLOperation).filter(hasValue);

/**
 * Returns a function that can be used to check for specific errorcodes in a graphql error object
 *
 * Example usage:
 * ```
 * const isItThisSpecificError = checkForGraphQlErrorCode(12345789);
 *
 * if (isItThisSpecificError(errorFromGraphQl)) {
 *     // do something...
 * }
 * ```
 */
export const makeCheckForGraphQlErrorCode =
    (errorCode: number) =>
    (error: any): boolean => {
        if (!error) {
            return false;
        }

        // Check if there are graphql errors
        if (!Array.isArray(error.graphQLErrors)) {
            return false;
        }

        return error.graphQLErrors.some(error => {
            return error?.extensions?.errorCode === errorCode;
        });
    };

export type RefetchQueries = NonNullable<MutationFunctionOptions['refetchQueries']>;

export enum EmptyCacheStrategy {
    SKIP_UPDATE = 'SKIP_UPDATE',
    ALLOW_OPTIONAL_CACHE = 'ALLOW_OPTIONAL_CACHE',
}

/**
 * Use this to update your cache after mutations!
 *
 * You have to pass two types to this generic function describing the cached query
 * you want to update <`CacheQuery`, `CacheQueryVariables`>.
 *
 * The updater will receive the current data in the cache in the shape of `CacheQuery`
 * and the result of the mutation which is inferred based on the mutation this is used in.
 * The `ReturnType` of `updater` has to match the shape of `CacheQuery`.
 *
 * @example
 * ```
 * myMutation({
 *   variables: {...},
 *   update: updateApolloCache<CacheQuery, CacheQueryVariables>()({
 *     cacheQuery: MyCacheQuery,
 *     cacheVariables: {...},
 *     updater: (cachedData, newData) => ({
 *       result: [newData, ...cachedData]
 *     })
 *   })
 * })
 * ```
 */
export type UpdateApolloCache<Query, Variables, Result> = {
    cacheQuery: DocumentNode;
    cacheVariables?: CacheVariables<Result, Variables>;
} & (
    | {
          emptyCacheStrategy: EmptyCacheStrategy.ALLOW_OPTIONAL_CACHE;
          updater: (cachedData: Query | null, result: Result) => Query;
      }
    | {
          emptyCacheStrategy?: EmptyCacheStrategy.SKIP_UPDATE;
          updater: (cachedData: Query, result: Result) => Query;
      }
);
export function updateApolloCache<CacheQuery, CacheVariables>() {
    return function _typeInferredFunction<Result>({
        cacheQuery,
        cacheVariables,
        ...rest
    }: UpdateApolloCache<CacheQuery, CacheVariables, Result>) {
        return function (cache: DataProxy, result: FetchResult<Result>) {
            if (!hasValue(result.data)) {
                return;
            }

            const variables = (() => {
                if (!hasValue(cacheVariables)) {
                    return undefined;
                }
                if (cacheVariablesIsTransform(cacheVariables)) {
                    return cacheVariables(result.data);
                }
                return cacheVariables;
            })();

            let cachedData: CacheQuery | null = null;
            try {
                cachedData = cache.readQuery<CacheQuery, CacheVariables>({
                    query: cacheQuery,
                    variables,
                });
            } catch (e) {
                // No cache available
            }

            switch (rest.emptyCacheStrategy) {
                case EmptyCacheStrategy.ALLOW_OPTIONAL_CACHE:
                    cache.writeQuery<CacheQuery, CacheVariables>({
                        query: cacheQuery,
                        variables,
                        data: rest.updater(cachedData, result.data),
                    });
                    break;
                default:
                    if (!cachedData) {
                        return;
                    }
                    cache.writeQuery<CacheQuery, CacheVariables>({
                        query: cacheQuery,
                        variables,
                        data: rest.updater(cachedData, result.data),
                    });
            }
        };
    };
}

type TransformVariables<Result, Variables> = (result: Result) => Variables | undefined;
export type CacheVariables<Result, Variables> = TransformVariables<Result, Variables> | Variables;

const cacheVariablesIsTransform = <Result, Variables>(
    cacheVariables: CacheVariables<Result, Variables>,
): cacheVariables is TransformVariables<Result, Variables> => {
    return typeof cacheVariables === 'function';
};

type CacheUpdater<T> = (proxy: DataProxy, mutationResult: FetchResult<T>) => void;

/**
 * Use this to compose several calls to `updateApolloCache` together.
 * @param updates at least one call to `updateApolloCache`
 * @returns void
 *
 * @example
 * ```
 * myMutation({
 *   variables: {...},
 *   update: composeUpdates(
 *     updateApolloCache<CacheQuery, CacheQueryVariables>()({
 *       cacheQuery: MyCacheQuery,
 *       cacheVariables: {...},
 *       updater: (cachedData, newData) => ({
 *         result: [newData, ...cachedData]
 *       })
 *     }),
 *     updateApolloCache<CacheQuery2, CacheQueryVariables2>()({
 *       cacheQuery: MyCacheQuery2,
 *       cacheVariables: {...},
 *       updater: (cachedData, newData) => ({
 *         result: [newData, ...cachedData]
 *       })
 *     }),
 *   )
 * })
 * ```
 */
export const composeUpdates =
    <Result>(...updates: NonEmptyArray<CacheUpdater<Result>>) =>
    (cache: DataProxy, result: FetchResult<Result>) =>
        updates.forEach(updater => updater(cache, result));
